<script setup>
    import { ref, useTemplateRef } from 'vue';
    import { useSqlEditorStore } from '@/stores/sqlEditorStore.ts';
    import { makeSqlUrl, makeSavedSqlUrl } from '@/apps/sqlResult/sqlResultUtils';
    import useCopyToClipboard from '@/shared/composables/useCopyToClipboard.js';

    const store = useSqlEditorStore();

    const defaultPageSize = 20;

    const optionQueryName = ref('');
    const optionPageSize = ref(defaultPageSize);
    const optionCopyByGuid = ref(false);
    const optionArguments = ref([]);

    const copyLinkDialog = useTemplateRef('copyLinkDialog');

    const { copyUrlToClipboard } = useCopyToClipboard();

    function initDialog() {
        if (store.queryIsLoaded) {
            optionCopyByGuid.value = true;
            optionQueryName.value = store.loadedQueryName;

            // Clone query parameters for a locally maintained list of copy link arguments
            optionArguments.value = store.loadedQueryParameters.map(x => ({
                name: x.name,
                stringValue: x.stringValue,
                included: true
            }));

            if (store.loadedQueryPageSize) {
                optionPageSize.value = store.loadedQueryPageSize;
            }
        } else {
            optionCopyByGuid.value = false;
            optionPageSize.value = defaultPageSize;
            optionQueryName.value = '';
        }
    }

    function onToggleArgument(argument) {
        argument.included = !argument.included;
    }

    function copyResultLink() {
        const sqlArguments = optionArguments.value.reduce((acc, a) => {
            if (a.included) {
                acc[a.name] = a.stringValue;
            }
            return acc;
        }, {});
        const optionQueryNameTrimmed = optionQueryName.value.trim();
        const url = store.queryIsLoaded && optionCopyByGuid.value
            ? makeSavedSqlUrl(
                store.loadedQueryGuid,
                store.loadedQueryName,
                sqlArguments,
                optionQueryNameTrimmed !== store.loadedQueryName && optionQueryNameTrimmed)
            : makeSqlUrl(
                store.loadedQuerySql,
                optionQueryNameTrimmed,
                optionPageSize.value,
                sqlArguments);

        copyUrlToClipboard(url, optionQueryName.value.trim() || 'Common Library query');
        close();
    }

    function close() {
        copyLinkDialog.value.toggle();
    }
</script>

<template>
    <b-dropdown
        ref="copyLinkDialog"
        position="is-bottom-right"
        append-to-body
        aria-role="dialog"
        trap-focus>
        <template #trigger>
            <a
                class="button"
                role="button"
                @click="initDialog">
                <b-icon
                    icon="share"
                    size="is-small" />
                <span>Share result link</span>
            </a>
        </template>

        <b-dropdown-item
            aria-role="listitem"
            :focusable="false"
            custom
            paddingless>
            <div
                class="card"
                style="width:400px;">
                <section class="card-content is-size-6">
                    <b-field
                        v-if="store.queryIsLoaded"
                        label="Use saved query">
                        <b-switch v-model="optionCopyByGuid">
                            If activated, any later changes to the query will be reflected when the link is accessed. If deactivated, the link
                            will contain the actual query as it is right now.
                        </b-switch>
                    </b-field>
                    <b-field
                        label="Query name"
                        message="The query name is used as a title when you copy the query link.">
                        <b-input
                            v-model="optionQueryName" />
                    </b-field>
                    <b-field
                        v-show="!optionCopyByGuid"
                        label="Page size"
                        message="The number of rows to show on each page when doing pagination of the result.">
                        <b-numberinput
                            v-model="optionPageSize"
                            min="1"
                            max="1000"
                            step="1"
                            controls-alignment="right"
                            controls-position="compact" />
                    </b-field>
                    <b-field
                        v-if="optionArguments.length > 0"
                        message="The above parameter values will be included in the link. You may remove any of these - the system will then ask the user for their values when the query is run.">
                        <table class="table is-narrow is-fullwidth">
                            <thead>
                                <tr>
                                    <th>Parameter</th>
                                    <th>Value</th>
                                    <th>&nbsp;</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="a in optionArguments">
                                    <td
                                        :class="{ 'is-strikeout': !a.included }">
                                        @{{ a.name }}
                                    </td>
                                    <td
                                        :class="{ 'is-strikeout': !a.included }">
                                        {{ a.stringValue }}
                                    </td>
                                    <td>
                                        <b-button
                                            size="is-small"
                                            class="is-pulled-right"
                                            @click="onToggleArgument(a)">
                                            {{ a.included ? 'Remove' : 'Include' }}
                                        </b-button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </b-field>
                </section>
                <footer class="card-footer">
                    <div class="card-footer-item is-justify-content-end">
                        <b-field grouped>
                            <button
                                class="button"
                                title="Cancel"
                                @click="close">
                                <span>Cancel</span>
                            </button>
                            <button
                                class="button is-primary"
                                title="Copy result link to clipboard"
                                @click="copyResultLink">
                                <b-icon
                                    icon="content-copy"
                                    size="is-small" />
                                <span>Copy link to clipboard</span>
                            </button>
                        </b-field>
                    </div>
                </footer>
            </div>
        </b-dropdown-item>
    </b-dropdown>
</template>
